import { Container, IDestroyOptions, Point, Rectangle, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { MyWalletResourcePackage } from "../../resources/MyWalletResourcePackage";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { Event } from "event-system";
import gsap from "gsap";
import { GlowFilter } from "pixi-filters";
import { WalletTabType } from "../WalletTabType";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";

export class MyWalletPanelElement extends Container {
    private static readonly IMAGE_SIDE_SIZE = 80;
    private static readonly SWITCH_TIME = 0.15;

    private readonly myWalletResources: MyWalletResourcePackage;
    private readonly initialPosition: Point;
    private readonly image: Sprite;
    private readonly text: Text;
    private readonly underline: Sprite;
    private readonly tabType: WalletTabType;
    private readonly animation: gsap.core.Timeline;
    private readonly yellowFilter: GlowFilter;
    private readonly pointer: Sprite;

    public set selected(selected: boolean) {
        this.underline.visible = selected;
        this.yellowFilter.enabled = selected;
        this.animation.fromTo(this.yellowFilter, { alpha: selected ? 0 : 1 }, { alpha: selected ? 1 : 0, duration: MyWalletPanelElement.SWITCH_TIME, ease: 'power1.inOut' });
        this.animation.fromTo(this.scale,
            {
                x: selected ? 1 : 1.2,
                y: selected ? 1 : 1.2
            },
            {
                x: selected ? 1.2 : 1,
                y: selected ? 1.2 : 1,
                duration: MyWalletPanelElement.SWITCH_TIME,
                ease: 'power1.inOut'
            },
            '<');
        this.animation.fromTo(this.position,
            {
                x: selected ? this.initialPosition.x : this.initialPosition.x - 10,
                y: selected ? this.initialPosition.y : this.initialPosition.y - 20
            },
            {
                x: selected ? this.initialPosition.x - 10 : this.initialPosition.x,
                y: selected ? this.initialPosition.y - 20 : this.initialPosition.y,
                duration: MyWalletPanelElement.SWITCH_TIME,
                ease: 'power1.inOut'
            },
            '<');
        this.animation.play();
        this.animation.then(() => this.animation.clear());
    }

    public set withPointer(value: boolean) {
        this.pointer.visible = value;
    }

    public readonly onClick: Event<WalletTabType>;

    public constructor(
        myWalletResources: MyWalletResourcePackage,
        commonResources: CommonResourcesPackage,
        tabType: WalletTabType,
        initialPosition: Point
    ) {
        super();
        this.tabType = tabType;
        this.myWalletResources = myWalletResources;
        this.initialPosition = initialPosition;
        this.animation = new gsap.core.Timeline({ paused: true });
        this.onClick = new Event();

        this.image = this.getImageByTab(tabType);
        this.image.width = this.image.height = MyWalletPanelElement.IMAGE_SIDE_SIZE;

        this.text = this.getTextByTab(tabType);
        this.text.position.set((this.image.width - this.text.width) * 0.5, this.image.height + 16);

        this.underline = this.createUnderline();
        this.underline.position.set(this.text.position.x, this.text.position.y + this.text.height - 5);
        this.underline.visible = false;

        this.pointer = new Sprite(commonResources.assets_common_ui_indicator_point_png);
        this.pointer.width = 20;
        this.pointer.height = 21;
        this.pointer.position.set(this.image.width - this.pointer.width * 0.5, -9);
        this.pointer.visible = false;

        this.addChild(this.image, this.text, this.underline, this.pointer);

        this.yellowFilter = new GlowFilter({ distance: 20, outerStrength: 2, innerStrength: 0, color: 0xffc200, quality: 0.1, alpha: 0.5 });
        this.yellowFilter.enabled = false;
        this.text.filters = [this.yellowFilter];
        this.underline.filters = [this.yellowFilter];

        this.on('pointerdown', this.onClickHandle, this);
        this.hitArea = new Rectangle(this.image.width <= this.text.width ? this.text.position.x : 0, 0, this.width, this.height);
        this.eventMode = 'static';
    }

    private getImageByTab(tab: WalletTabType): Sprite {
        switch (tab) {
            case WalletTabType.PIGGY_CLUB: return new Sprite(this.myWalletResources.assets_views_my_wallet_ui_piggy_icon_png);
            case WalletTabType.BUILDING: return new Sprite(this.myWalletResources.assets_views_my_wallet_ui_building_icon_png);
            case WalletTabType.DICE_ROLLS: return new Sprite(this.myWalletResources.assets_views_my_wallet_ui_dice_roll_icon_png);
            case WalletTabType.TOKENS: return new Sprite(this.myWalletResources.assets_views_my_wallet_ui_tokens_icon_png);
        }
    }

    private getTextByTab(tab: WalletTabType): Text {
        switch (tab) {
            case WalletTabType.PIGGY_CLUB: return new Text('PIGGY CLUB', new TabTextStyle());
            case WalletTabType.BUILDING: return new Text('BUILDINGS', new TabTextStyle());
            case WalletTabType.DICE_ROLLS: return new Text('DICE ROLLS', new TabTextStyle());
            case WalletTabType.TOKENS: return new Text('TOKENS', new TabTextStyle());
        }
    }

    private createUnderline(): Sprite {
        const underline = new Sprite(Texture.WHITE);
        underline.tint = 'FFFFFF';
        underline.width = this.text.width;
        underline.height = 3;
        return underline;
    };

    private onClickHandle(): void {
        this.onClick.emit(this.tabType);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off('pointerdown', this.onClickHandle, this);
        this.animation.kill();
        super.destroy(options);
    }
}

class TabTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40,
            fill: 'FFFFFF'
        });
    }
}
